import { useState, useCallback } from 'react';
import { useForm, SubmitHandler, Controller } from 'react-hook-form';
import { useMutation, useQuery } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers';
import * as yup from 'yup';
import cn from 'classnames';
// Hooks
import { useConfirmCardPayment } from 'hooks';
import { useGetCurrUser } from 'hooks/useGetCurrUser';
import { useBtcPrice } from 'hooks/useBtcPrice';
// Api
import { CREATE_STORE_TIP, CREATE_USER_TIP_BITCOIN } from 'api/store/mutations';
import { GET_TIP_MESSAGE_PRESETS } from 'api/tips/queries';
// Types
import {
  CreateUserTip,
  CreateUserTipVariables,
  CreateUserTip_createUserTip,
} from 'api/store/types/CreateUserTip';
import {
  CreateUserTipBitcoin,
  CreateUserTipBitcoinVariables,
  CreateUserTipBitcoin_createUserTipBitcoin_payment_CryptoPayment,
} from 'api/store/types/CreateUserTipBitcoin';
import {
  GetMediaPost_getMediaPost_VideoPost_store,
  GetMediaPost_getMediaPost_ImagePost_store,
} from 'api/mediaPost/types/GetMediaPost';
import { GetStoreBySlug_store } from 'api/store/types/GetStoreBySlug';
import { GetStream_stream_store } from 'api/streams/types/GetStream';
import { GetTipMessagePresets_getTipMessagePresets } from 'api/tips/types/GetTipMessagePresets';
// Constants
import { TERMS_OF_SERVICE } from 'constants/routes';
// Components
import BitcoinPaymentDisplay from 'components/BitcoinPayment/BitcoinPaymentDisplay';
import { showToast } from 'components/common/Toast/Toast';
import ProductPayment from 'components/common3/ProductPayment/ProductPayment';
// Ui3
import Button from 'ui3/Button/Button';
import TextField from 'ui3/TextField/TextField';
import Select from 'ui3/Select/Select';
import Text from 'ui3/Text/Text';
import CheckBox from 'ui3/CheckBox/CheckBox';
// Styles
import styles from './TipAthlete.module.scss';

type TipSuccessData = {
  id: string;
  amount: number;
  message?: string | null;
  storeName: string;
  store: GeneralTipProps['store'];
};

type GeneralTipProps = {
  store:
    | GetStoreBySlug_store
    | GetStream_stream_store
    | GetMediaPost_getMediaPost_VideoPost_store
    | GetMediaPost_getMediaPost_ImagePost_store;
  onTipSuccess?: (tipData: TipSuccessData) => void;
  onBitcoinPaymentConfirmed?: () => void;
};

type FormInputs = {
  amount: number;
  message: string;
};

export type TipMessagePresetSelectOption = {
  value: string;
  label: string;
};

type PaymentType = 'cash' | 'crypto';

const cashValidationSchema = yup.object().shape({
  amount: yup
    .number()
    .min(1, 'Please enter an amount higher than $1.00')
    .transform((value) => (isNaN(value) ? undefined : Number(value)))
    .required('Tip Amount is required field.'),
  message: yup
    .string()
    .max(1000, "Maximum number of characters can't be more than 1000"),
});

const cryptoValidationSchema = yup.object().shape({
  amount: yup
    .number()
    .min(0.0001, 'Please enter an amount higher than ₿0.0001')
    .transform((value) => (isNaN(value) ? undefined : Number(value)))
    .test('max-decimals', 'Amount can have up to 8 decimal places', (value) => {
      if (value === undefined || value === null) return true;
      const decimals = value.toString().split('.')[1];
      return !decimals || decimals.length <= 8;
    })
    .required('Tip Amount is required field.'),
  message: yup
    .string()
    .max(1000, "Maximum number of characters can't be more than 1000"),
});

const defaultErrorMessage =
  'Looks like something went wrong. Please double-check all fields and try again';

const PRESET_AMOUNTS = [5, 10, 25, 50, 100];

const CRYPTO_OPTIONS = ['BTC'];

const cryptoIconMap = {
  BTC: 'bitcoin',
};

const TipAthlete = ({
  store,
  onTipSuccess,
  onBitcoinPaymentConfirmed,
}: GeneralTipProps) => {
  const [paymentMethodId, setPaymentMethodId] = useState<string>('');
  const [selectedPresetAmount, setSelectedPresetAmount] = useState<
    number | null
  >(null);
  const [paymentType, setPaymentType] = useState<PaymentType>('cash');
  const [cryptoPaymentData, setCryptoPaymentData] = useState<{
    invoiceUrl: string;
    tipId: string;
    amount: number;
  } | null>(null);
  const [showBitcoinPayment, setShowBitcoinPayment] = useState(false);
  const [tosAccepted, setTosAccepted] = useState<boolean>(false);

  const { data: user } = useGetCurrUser();
  const {
    fetchBtcPrice,
    convertBtcToUsd,
    loading: priceLoading,
  } = useBtcPrice();

  const hasAcceptedTos = user?.me?.termsOfServiceOptIn;

  const [createStoreTip, { loading: createStoreTipLoading }] = useMutation<
    CreateUserTip,
    CreateUserTipVariables
  >(CREATE_STORE_TIP);

  const [
    createUserTipBitcoin,
    { loading: createUserTipBitcoinLoading },
  ] = useMutation<CreateUserTipBitcoin, CreateUserTipBitcoinVariables>(
    CREATE_USER_TIP_BITCOIN
  );

  const {
    confirmCardPayment,
    loading: stripeLoading,
  } = useConfirmCardPayment();

  const showError = (message: string = defaultErrorMessage) => {
    showToast({
      message,
      type: 'error',
    });
  };

  const storeName = 'this creator';

  const {
    watch,
    reset,
    register,
    handleSubmit,
    setValue,
    control,
    errors: formErrors,
  } = useForm<FormInputs>({
    resolver: yupResolver(
      paymentType === 'crypto' ? cryptoValidationSchema : cashValidationSchema
    ),
    mode: 'onChange',
  });

  const amount = watch('amount');
  const message = watch('message');

  const { data: presetsData } = useQuery(GET_TIP_MESSAGE_PRESETS);
  const presets = (presetsData?.getTipMessagePresets || []).filter(
    (p: GetTipMessagePresets_getTipMessagePresets) => p.isActive
  );

  const handlePresetAmountClick = (presetAmount: number) => {
    if (selectedPresetAmount === presetAmount) {
      setValue('amount', undefined);
      setSelectedPresetAmount(null);
    } else {
      setValue('amount', presetAmount);
      setSelectedPresetAmount(presetAmount);
    }
  };

  const handleAmountInputChange = useCallback(() => {
    setSelectedPresetAmount(null);
    if (paymentType === 'crypto') {
      fetchBtcPrice();
    }
  }, [paymentType, fetchBtcPrice]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === ',' || e.key === '-' || e.key === '+' || e.key === 'e') {
      e.preventDefault();
    }
    if (e.key === '.') {
      if (paymentType !== 'crypto') {
        e.preventDefault();
      }
    }
  };

  const handlePaymentSuccess = async (
    tipData?: CreateUserTip_createUserTip
  ): Promise<void> => {
    if (onTipSuccess) {
      onTipSuccess({
        amount,
        message,
        storeName,
        store,
        id: tipData?.id || '',
        ...tipData,
      });
    }
    reset();
  };

  const handleCashPayment: SubmitHandler<FormInputs> = async (): Promise<void> => {
    try {
      if (paymentMethodId) {
        if (!hasAcceptedTos && !tosAccepted) {
          showError('You must agree to the Tipping Terms to continue');
          return;
        }
        const { data } = await createStoreTip({
          variables: {
            input: {
              recipientId: store?.id,
              amount: +amount,
              message: message,
              termsOfServiceOptIn: tosAccepted,
            },
          },
        });
        const stripeIntentClientSecret =
          data?.createUserTip?.stripeIntentClientSecret;
        if (stripeIntentClientSecret) {
          await confirmCardPayment({
            paymentMethodId,
            stripeIntentClientSecret,
            onErrorCallback: showError,
            onSuccessCallback: () => handlePaymentSuccess(data?.createUserTip),
          });
        } else {
          showError();
        }
      }
    } catch (err) {
      showError();
    }
  };

  const handleCryptoPayment: SubmitHandler<FormInputs> = async (): Promise<void> => {
    try {
      if (!hasAcceptedTos && !tosAccepted) {
        showError('You must agree to the Tipping Terms to continue');
        return;
      }
      const { data } = await createUserTipBitcoin({
        variables: {
          input: {
            recipientId: store?.id,
            amount: +amount,
            message: message,
            termsOfServiceOptIn: tosAccepted,
          },
        },
      });

      const tipId = data?.createUserTipBitcoin?.id;
      const payment = data?.createUserTipBitcoin?.payment;

      if (payment?.__typename === 'CryptoPayment' && tipId) {
        const paymentWithNewFields = payment as CreateUserTipBitcoin_createUserTipBitcoin_payment_CryptoPayment;
        const { bitcoinInvoiceUrl } = paymentWithNewFields;

        if (bitcoinInvoiceUrl) {
          setCryptoPaymentData({
            invoiceUrl: bitcoinInvoiceUrl,
            tipId,
            amount: +amount,
          });

          setShowBitcoinPayment(true);
        } else {
          throw new Error('No Bitcoin invoice URL received');
        }
      } else {
        showError('Failed to create Bitcoin invoice');
      }
    } catch (err) {
      showError();
    }
  };

  const handlePayment: SubmitHandler<FormInputs> = async (
    data
  ): Promise<void> => {
    if (paymentType === 'cash') {
      await handleCashPayment(data);
    } else {
      await handleCryptoPayment(data);
    }
  };

  const handlePaymentTypeChange = (type: string) => {
    setPaymentType(type as PaymentType);
    reset();
  };

  const isCtaLoading =
    stripeLoading || createStoreTipLoading || createUserTipBitcoinLoading;
  const isDisabled = paymentType === 'cash' ? !paymentMethodId : false;

  const handleConfettiStart = () => {
    if (onBitcoinPaymentConfirmed) {
      onBitcoinPaymentConfirmed();
    }
  };

  const handleOnPaymentSent = () => {
    setShowBitcoinPayment(false);
    setCryptoPaymentData(null);
    if (onTipSuccess) {
      onTipSuccess({
        amount,
        message,
        storeName,
        store,
        id: cryptoPaymentData?.tipId || '',
      });
    }
    reset();
  };

  if (!store) {
    return null;
  }

  if (showBitcoinPayment && cryptoPaymentData) {
    return (
      <>
        <BitcoinPaymentDisplay
          invoiceUrl={cryptoPaymentData.invoiceUrl}
          tipId={cryptoPaymentData.tipId}
          amount={cryptoPaymentData.amount}
          orderType="user_tip"
          onPaymentSent={handleOnPaymentSent}
          onConfettiStart={handleConfettiStart}
        />
      </>
    );
  }

  return (
    <form aria-label={`Tip to ${storeName}`}>
      <Text className={styles.header}>
        {`Send a tip to ${storeName} to show your appreciation!`}
      </Text>

      {/* Payment Type Tabs */}
      <div className={styles.paymentTypeTabs}>
        <Button
          type="button"
          variant={'secondary'}
          className={cn(styles.tabButton, {
            [styles.tabButtonActive]: paymentType === 'cash',
          })}
          onClick={() => handlePaymentTypeChange('cash')}
        >
          Cash
        </Button>
        <Button
          type="button"
          variant={'secondary'}
          className={cn(styles.tabButton, {
            [styles.tabButtonActive]: paymentType === 'crypto',
          })}
          onClick={() => handlePaymentTypeChange('crypto')}
        >
          Crypto
        </Button>
      </div>

      <div className={styles.body}>
        <div className={styles.bodySection}>
          <div className={styles.tipAmountSection}>
            <Text className={styles.subject} variant="body2Medium">
              Tipping Amount
            </Text>
            {paymentType === 'crypto' ? (
              <div style={{ position: 'relative' }}>
                <TextField
                  className={styles.amountInput}
                  name="amount"
                  ref={register}
                  placeholder="0 BTC"
                  prefixIcon="bitcoin"
                  error={formErrors?.amount?.message}
                  type="number"
                  step="0.00000001"
                  onKeyDown={handleKeyDown}
                  onChange={handleAmountInputChange}
                />
                {amount > 0 && !formErrors?.amount && (
                  <div className={styles.usdValue}>
                    <Text variant="body1Regular16" color="lights-low">
                      {priceLoading
                        ? '...'
                        : convertBtcToUsd(amount)
                        ? `~$${convertBtcToUsd(amount)?.toLocaleString()}`
                        : ''}
                    </Text>
                  </div>
                )}
              </div>
            ) : (
              <TextField
                className={styles.amountInput}
                name="amount"
                ref={register}
                prefixIcon="dollar"
                placeholder="0"
                error={formErrors?.amount?.message}
                type="number"
                onKeyDown={handleKeyDown}
                onChange={handleAmountInputChange}
              />
            )}

            {paymentType === 'cash' && (
              <div className={styles.presetAmounts}>
                {PRESET_AMOUNTS.map((presetAmount) => (
                  <Button
                    key={presetAmount}
                    type="button"
                    size="large"
                    variant="secondary"
                    className={cn(styles.presetAmountButton, {
                      [styles.presetAmountButtonActive]:
                        selectedPresetAmount === presetAmount,
                    })}
                    onClick={() => handlePresetAmountClick(presetAmount)}
                  >
                    ${presetAmount}
                  </Button>
                ))}
              </div>
            )}

            {paymentType === 'crypto' && (
              <div className={styles.cryptoContainer}>
                <Text variant="captionRegular" className={styles.minAmountNote}>
                  Min. amount 0.0001 BTC
                </Text>
                <div className={styles.presetCrypto}>
                  {CRYPTO_OPTIONS.map((cryptoOption) => (
                    <Button
                      key={cryptoOption}
                      type="button"
                      size="large"
                      variant="secondary"
                      icon={cryptoIconMap[cryptoOption]}
                      className={cn(
                        styles.presetCryptoOptionButton,
                        styles.presetAmountButtonActive
                      )}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>

          <Text variant="body2Medium">Message</Text>
          <Controller
            control={control}
            name="message"
            render={({ onChange, value }) => (
              <Select
                placeholder="Select your message"
                options={presets.map(
                  (p: GetTipMessagePresets_getTipMessagePresets) => ({
                    label: p.text,
                    value: p.text,
                  })
                )}
                value={value ? { label: value, value } : null}
                onChange={(opt: TipMessagePresetSelectOption) =>
                  onChange(opt ? opt.value : '')
                }
                isClearable
                error={formErrors?.message?.message}
              />
            )}
          />
        </div>

        {paymentType === 'cash' && (
          <div className={styles.paymentSection}>
            <ProductPayment onSelect={setPaymentMethodId} />
          </div>
        )}
      </div>

      <div className={styles.footer}>
        {!hasAcceptedTos && (
          <div className={styles.tosRow}>
            <CheckBox
              name="tos"
              checked={tosAccepted}
              className={styles.tosCheckbox}
              onChange={(e) => setTosAccepted(e.target.checked)}
              label=""
            />
            <Text className={styles.tosText}>
              I understand this Tip is a voluntary gift with no goods, services,
              or benefits in return.{' '}
              <a
                href={TERMS_OF_SERVICE}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.tosLink}
                onClick={(e) => e.stopPropagation()}
              >
                Learn more
              </a>
            </Text>
          </div>
        )}
        <Button
          onClick={handleSubmit(handlePayment)}
          loading={isCtaLoading}
          disabled={isDisabled || (!hasAcceptedTos && !tosAccepted)}
          type="submit"
        >
          Send Tip
        </Button>
      </div>
    </form>
  );
};

export default TipAthlete;
